import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import { parseFirebaseDate, formatFirebaseDate } from '../../utils/dateUtils';
import {
  UserProfile,
  UserRole,
  accessControlService,
  USER_ROLES,
  FOUNDING_ADMIN_UIDS,
} from '@watchlist/shared';
import InitialsAvatar from '../widgets/InitialsAvatar';
import UserRoleChip from './UserRoleChip';
import UserDetailDialog from './UserDetailDialog';
import CustomTabs from '../widgets/CustomTabs';

interface UserManagementTabProps {
  adminUid: string;
}

const UserManagementTab: React.FC<UserManagementTabProps> = ({ adminUid }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'blocked'>('all');
  const [processingUid, setProcessingUid] = useState<string | null>(null);

  // Role change dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [newRole, setNewRole] = useState<UserRole>('regular');

  // User detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<UserProfile | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const allUsers = await accessControlService.getAllUsersWithRoles(500);
      setUsers(allUsers);
      setFilteredUsers(allUsers);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    let base = activeTab === 'blocked'
      ? users.filter(u => (u as any).isBlocked)
      : users;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      base = base.filter(
        u =>
          u.displayName?.toLowerCase().includes(lower) ||
          u.email?.toLowerCase().includes(lower) ||
          u.username?.toLowerCase().includes(lower)
      );
    }
    setFilteredUsers(base);
  }, [searchTerm, users, activeTab]);

  const handleRoleClick = (user: UserProfile) => {
    setSelectedUser(user);
    setNewRole(user.role || 'regular');
    setRoleDialogOpen(true);
  };

  const handleUserClick = (user: UserProfile) => {
    setDetailUser(user);
    setDetailDialogOpen(true);
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;

    setProcessingUid(selectedUser.uid);
    try {
      await accessControlService.updateUserRole(selectedUser.uid, newRole, adminUid);

      // Update local state
      setUsers(prev =>
        prev.map(u => (u.uid === selectedUser.uid ? { ...u, role: newRole } : u))
      );

      setRoleDialogOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Error updating role:', err);
      setError(err.message || 'Failed to update role');
    } finally {
      setProcessingUid(null);
    }
  };

  const formatDate = (timestamp: any) => {
    return formatFirebaseDate(timestamp, 'Unknown', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isFoundingAdmin = (uid: string) => FOUNDING_ADMIN_UIDS.includes(uid);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {activeTab === 'blocked' ? 'Blocked Users' : 'All Users'} ({filteredUsers.length})
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadUsers}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Box>

      {/* All / Blocked tabs */}
      <Box sx={{ mb: 2 }}>
        <CustomTabs
          value={activeTab === 'all' ? 0 : 1}
          onChange={(v) => setActiveTab(v === 0 ? 'all' : 'blocked')}
          tabs={[
            { label: `All (${users.length})`, value: 0 },
            { label: `Blocked (${users.filter(u => (u as any).isBlocked).length})`, value: 1 },
          ]}
        />
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search by name, email, or username..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: theme.palette.text.disabled }} />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Users List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {filteredUsers.map(user => (
          <Box
            key={user.uid}
            onClick={() => handleUserClick(user)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              },
            }}
          >
            {/* Avatar */}
            {user.photoURL ? (
              <Box
                component="img"
                src={user.photoURL}
                alt=""
                sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }}
              />
            ) : (
              <InitialsAvatar name={user.displayName} size={40} />
            )}

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: theme.palette.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.displayName}
                </Typography>
                {user.username && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.palette.text.disabled,
                    }}
                  >
                    @{user.username}
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user.email}
              </Typography>
              <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled }}>
                Joined {formatDate(user.createdAt)}
              </Typography>
            </Box>

            {/* Role Chip (clickable) */}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                if (!isFoundingAdmin(user.uid)) {
                  handleRoleClick(user);
                }
              }}
              sx={{
                cursor: isFoundingAdmin(user.uid) ? 'default' : 'pointer',
                opacity: isFoundingAdmin(user.uid) ? 0.7 : 1,
                '&:hover': isFoundingAdmin(user.uid)
                  ? {}
                  : {
                    opacity: 0.8,
                  },
              }}
              title={isFoundingAdmin(user.uid) ? 'Founding admin (cannot change role)' : 'Click to change role'}
            >
              <UserRoleChip role={user.role || 'regular'} />
            </Box>
          </Box>
        ))}

        {filteredUsers.length === 0 && (
          <Typography sx={{ textAlign: 'center', py: 4, color: theme.palette.text.secondary }}>
            {searchTerm ? 'No users found matching your search' : 'No users found'}
          </Typography>
        )}
      </Box>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {selectedUser.photoURL ? (
                  <Box
                    component="img"
                    src={selectedUser.photoURL}
                    alt=""
                    sx={{ width: 48, height: 48, borderRadius: '50%' }}
                  />
                ) : (
                  <InitialsAvatar name={selectedUser.displayName} size={48} />
                )}
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{selectedUser.displayName}</Typography>
                  <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>

              <FormControl fullWidth>
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 500 }}>Select Role</Typography>
                <Select
                  value={newRole}
                  onChange={e => setNewRole(e.target.value as UserRole)}
                  size="small"
                >
                  <MenuItem value={USER_ROLES.ADMIN}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserRoleChip role="admin" size="small" />
                      <Typography sx={{ fontSize: 13 }}>Full access + admin console</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={USER_ROLES.MODERATOR}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserRoleChip role="moderator" size="small" />
                      <Typography sx={{ fontSize: 13 }}>Crew posts + moderator console (no user admin)</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={USER_ROLES.TEST}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserRoleChip role="test" size="small" />
                      <Typography sx={{ fontSize: 13 }}>Tester access</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={USER_ROLES.REGULAR}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserRoleChip role="regular" size="small" />
                      <Typography sx={{ fontSize: 13 }}>Standard user</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={USER_ROLES.PENDING}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserRoleChip role="pending" size="small" />
                      <Typography sx={{ fontSize: 13 }}>Revoke access</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {newRole === USER_ROLES.PENDING && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This will revoke the user's access to the app. They will need to request access again.
                </Alert>
              )}

              {newRole === USER_ROLES.ADMIN && selectedUser.uid !== adminUid && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This user will have full admin privileges including access to this console.
                </Alert>
              )}

              {newRole === USER_ROLES.MODERATOR && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Moderators can publish official Crew feed posts and open the moderator console. They cannot manage users or access requests.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleRoleChange}
            variant="contained"
            disabled={processingUid === selectedUser?.uid || newRole === selectedUser?.role}
            sx={{ textTransform: 'none' }}
          >
            {processingUid === selectedUser?.uid ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <UserDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        user={detailUser}
        onUserUpdated={(uid, changes) => {
          setUsers(prev => prev.map(u => u.uid === uid ? { ...u, ...changes } : u));
          setDetailDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default UserManagementTab;
